import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import { Toggle } from "@carbon/react";
import TextField from "../carbon/Form/TextField";
import SelectField, { SelectFieldOption } from "../carbon/Form/SelectField";
import TextareaField from "../carbon/Form/TextareaField";
import { NewMaterialFormValues } from "./newMaterialFormValidationSchema";
import { MEASUREMENT_UNIT_LIST, TO_BE_AGREED } from "../../utils";
import i18next from "../../i18n";
import { SiteResponse } from "../../types";

export interface MaterialGoodsFieldsProps {
  control: Control<NewMaterialFormValues>;
  /** The current `measurementUnit` value, watched by the parent (`useWatch`) — decides whether
   * `customMeasurementUnit` renders at all. Passed down rather than watched again in here so
   * there's exactly one subscription driving this decision. */
  measurementUnit: string;
  sites: SiteResponse[] | undefined;
  materialConfidential: boolean;
  onMaterialConfidentialChange: (confidential: boolean) => void;
}

// `MEASUREMENT_UNIT_LIST` (utils/enums.ts) carries two distinct entries with the identical name
// "M" (ids 6 and 9) — a pre-existing data quality quirk, not introduced here. The pre-Carbon
// `<option>` loop hid the resulting React key collision with a `${unit.name}-${index}` compound
// key while still rendering both (functionally identical, since both submit the same value "M").
// `SelectField` keys its `SelectItem`s by `option.value` alone (no index available to it), so
// this de-dupes by name before building the option list — same submittable values, one fewer
// visually-redundant duplicate entry, and no React key warning.
const measurementUnitOptions: SelectFieldOption[] = Array.from(
  new Map(
    (MEASUREMENT_UNIT_LIST ?? []).map((unit) => [unit.name, unit.name])
  ).keys()
).map((name) => ({ value: name, label: name }));

/**
 * The Goods-only field group of the create-material form: everything the pre-Carbon
 * `NewMaterialForm.tsx` rendered ONLY when `materialCreationType === "GOOD"` (the Services kind
 * only ever needed `ean`/`globalMaterialName`, both rendered by the parent). Split out purely to
 * keep `NewMaterialForm.tsx` under the 400-line budget — this owns no state of its own beyond
 * what `Controller` already manages through `control`.
 *
 * `measurementUnit`/`deliveryPlace` are the two `<select>`s the pre-Carbon form's own historic bug
 * lived on: both spread `{...register(name, {required: true})}` and then immediately overrode it
 * with a manual `onChange={(e) => setXxxValue(e.target.value)}` — the later prop wins, so RHF's
 * own registered `onChange` never ran on a real change event and a selection never reached RHF's
 * internal store, even though the DOM value looked correct. `SelectField` (this codebase's shared
 * Carbon primitive) does not reproduce that: it renders `{...field}` from `Controller` with
 * nothing after it to override `onChange`, so this component adds NO manual `onChange` prop to
 * either `<Select>` at all — the fix is the absence of the old bug's exact shape, not a different
 * workaround for it.
 *
 * Majority-required marking (Carbon's own "(optional)"-on-the-minority convention — see
 * `TextField`'s docstring): of this form's 8 text/select fields (`ean`, `globalMaterialName`,
 * `measurementUnit`, `deliveryFormat`, `deliveryPlace`, `brand`, `details`, `comments`), 4 are
 * required and 4 are optional — a tie broken toward "majority required" once
 * `customMeasurementUnit`'s conditional requirement is counted, matching every other Carbon form
 * in this codebase so far (all majority-required). `deliveryFormat`/`brand`/`details`/`comments`
 * are the marked minority here.
 */
const MaterialGoodsFields = ({
  control,
  measurementUnit,
  sites,
  materialConfidential,
  onMaterialConfidentialChange,
}: MaterialGoodsFieldsProps) => {
  const { t } = useTranslation();

  const deliveryPlaceOptions: SelectFieldOption[] = [
    ...(sites ?? []).map((site) => ({ value: site.id, label: site.name })),
    { value: TO_BE_AGREED, label: TO_BE_AGREED },
  ];

  const showCustomMeasurementUnit =
    measurementUnit === i18next.t("measurementUnits.other");

  return (
    <div className="new-material-form__goods-grid">
      <SelectField
        name="measurementUnit"
        label={t("newMaterialForm.measurementUnit")}
        control={control}
        options={measurementUnitOptions}
        placeholder={t("newMaterialForm.selectMeasurementUnit")}
        required
      />

      {showCustomMeasurementUnit && (
        <TextField
          name="customMeasurementUnit"
          label={t("newMaterialForm.customMeasurementUnit")}
          placeholder={t("newMaterialForm.enterCustomMeasurementUnit")}
          control={control}
          required
        />
      )}

      <TextField
        name="deliveryFormat"
        label={t("newMaterialForm.deliveryFormat")}
        control={control}
        optional
      />

      <SelectField
        name="deliveryPlace"
        label={t("newMaterialForm.deliveryPlace")}
        control={control}
        options={deliveryPlaceOptions}
        placeholder={t("newMaterialForm.selectDeliveryPlace")}
        required
      />

      <TextField
        name="brand"
        label={t("newMaterialForm.brand")}
        control={control}
        optional
      />

      <div className="new-material-form__toggle-field">
        <Toggle
          id="materialConfidential"
          labelText={t("newMaterialForm.confidentialMaterial")}
          toggled={materialConfidential}
          onToggle={onMaterialConfidentialChange}
        />
      </div>

      <TextareaField
        name="details"
        label={t("newMaterialForm.details")}
        control={control}
        optional
        rows={3}
        enableCounter
        maxCount={255}
        className="new-material-form__span-all"
      />

      <TextField
        name="comments"
        label={t("newMaterialForm.comments")}
        control={control}
        optional
        className="new-material-form__span-all"
      />
    </div>
  );
};

export default MaterialGoodsFields;
